import { useState, useEffect } from 'react'
import { Loader2 } from "lucide-react"
import { useNavigate } from 'react-router-dom'
import { Logos3 } from "@/components/ui/logos3"
import { useAnalytics } from '@/lib/analytics'
import PricingPlansDisplay from '@/components/shared/PricingPlansDisplay'

interface Plan {
    id: string
    product_name: string
    product_description: string
    price: number
    currency: string
    interval: 'month' | 'year'
    features: (string | { name: string; })[]
    structured_features?: {
        max_brands?: string
        monthly_credits?: string
        features?: string
        [key: string]: any
    }
}

export default function PricingPage() {
    const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly')
    const [plans, setPlans] = useState<Plan[]>([])
    const [plansLoading, setPlansLoading] = useState(true)
    const navigate = useNavigate()
    const { trackEvent, events } = useAnalytics()

    // Fetch plans on component mount
    useEffect(() => {
        // Track pricing page view
        trackEvent(events.SUBSCRIPTION_VIEWED, {
            page: 'pricing',
            referrer: document.referrer
        });
        
        const fetchPlans = async () => {
            try {
                setPlansLoading(true)
                const workerUrl = import.meta.env.VITE_WORKERS_URL
                const response = await fetch(`${workerUrl}/billing-functions/get_plans_public`, {
                    method: 'POST',
                    body: JSON.stringify({ account_id: 'public' }) // Special case for public pricing
                })

                if (!response.ok) {
                    throw new Error('Failed to fetch plans')
                }

                const data = await response.json()
                // Transform the plan details into the format expected by PricingPlansDisplay
                const transformedPlans = data.map((plan: any) => ({
                    id: plan.id,
                    product_name: plan.product_name,
                    product_description: plan.product_description,
                    price: plan.price,
                    currency: plan.currency,
                    interval: plan.interval === 'month' ? 'month' : 'year',
                    features: plan.features,
                    structured_features: plan.structured_features || {}
                }))
                
                // Track plans loaded
                trackEvent(events.FEATURE_USED, {
                    feature: 'plans_loaded',    
                    plan_count: data.length,
                    has_monthly: data.some((p: any) => p.interval === 'month'),
                    has_yearly: data.some((p: any) => p.interval === 'year')
                });
                
                setPlans(transformedPlans)
            } catch (error) {
                console.error('Error fetching plans:', error)
                
                // Track error
                trackEvent('error_occurred', {
                    error_type: 'plans_fetch_error',
                    error_message: error instanceof Error ? error.message : String(error)
                });
            } finally {
                setPlansLoading(false)
            }
        }

        fetchPlans()
    }, [trackEvent, events])

    const handlePlanSelection = (planId: string, planName: string, planPrice: number, isEnterprise?: boolean) => {
        if (isEnterprise) {
            // Track enterprise contact sales click
            trackEvent(events.CHECKOUT_STARTED, {
                plan_id: 'enterprise',
                plan_name: 'Enterprise',
                plan_price: 'custom',
                billing_interval: billingInterval
            });
            
            // Navigate to contact page with pricing information topic
            navigate('/contact?topic=Pricing%20Information')
        } else {
            // Track plan selection
            trackEvent(events.CHECKOUT_STARTED, {
                plan_id: planId,
                plan_name: planName,
                plan_price: planPrice,
                billing_interval: billingInterval
            });
            
            // Update the redirect to include both plan and flow parameters
            navigate(`/login?flow=signup&plan=${planId}`)
        }
    }

    if (plansLoading) {
        return (
            <div className="flex justify-center items-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative">
                {/* Enhanced Header Section */}
                <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 border-b border-blue-200/50 shadow-sm">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />
                    
                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                        <div className="text-center space-y-8">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent leading-tight pb-2">
                                Plans to maximize your growth
                            </h1>
                            
                            <p className="text-gray-600 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed">
                                Choose the perfect plan for your needs. All plans include a 14-day free trial.
                            </p>
                        </div>
                    </div>
                </div>
                
                {/* Pricing Plans Display */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 pt-16">
                    <PricingPlansDisplay
                        plans={plans}
                        billingInterval={billingInterval}
                        onBillingIntervalChange={(interval) => {
                            trackEvent(events.FEATURE_USED, {
                                feature: 'billing_interval_changed',
                                previous: billingInterval,
                                new: interval
                            });
                            setBillingInterval(interval);
                        }}
                        onPlanSelect={handlePlanSelection}
                        loading={plansLoading}
                        showComparisonTable={true}
                        variant="public"
                        className="pt-6"
                    />
                </div>
                
                {/* Company Logos Section */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 sm:pb-16">
                    <div className="text-center space-y-6 sm:space-y-8">
                        <div className="relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm p-6 sm:p-8">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-purple-50/20" />
                            <div className="relative">
                                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
                                    Welcome to the growing list of companies growing with Rainfactory
                                </p>
                                <Logos3 />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}